import { User, Mail, Phone, Pencil, Building2, DollarSign, Globe, Building } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';










import { updateCustomerCompanyDetails } from '../../../../../services/staffingService';

// Validation schema for the form
const companyDetailsSchema = yup.object().shape({
  companyLegalName: yup.string().required('Company legal name is required'),
  companyFriendlyName: yup.string().required('Company friendly name is required'),
  hourlyBillingRate: yup.number().required('Hourly billing rate is required').min(45, 'Hourly rate should be at least $45').max(500, 'Cannot exceed $500'),
  companyWebsiteUrl: yup.string().url('Invalid URL').nullable(),
  facilityType: yup.string().required('Facility type is required'),
  contactPersonName: yup.string().required('Contact person name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
});

const ContactInfo = ({ initialCompanyData, providerCompanyId }) => {
  const [companyData, setCompanyData] = useState(initialCompanyData);
  const [isEditing, setIsEditing] = useState(false);

  const { register, handleSubmit, control, formState: { errors }, reset } = useForm({
    resolver: yupResolver(companyDetailsSchema),
    defaultValues: {
      companyLegalName: initialCompanyData?.companyLegalName || '',
      companyFriendlyName: initialCompanyData?.companyFriendlyName || '',
      hourlyBillingRate: initialCompanyData?.preferences.hourlyBillingRate || '',
      companyWebsiteUrl: initialCompanyData?.companyWebsiteUrl || '',
      facilityType: initialCompanyData?.facilityType || '',
      contactPersonName: initialCompanyData?.contact?.contactPersonName || '',
      email: initialCompanyData?.contact?.email || '',
      phone: initialCompanyData?.contact?.phone || '',
    },
  });

  useEffect(() => {
    setCompanyData(initialCompanyData);
    reset({
      companyLegalName: initialCompanyData?.companyLegalName || '',
      companyFriendlyName: initialCompanyData?.companyFriendlyName || '',
      hourlyBillingRate: initialCompanyData?.preferences.hourlyBillingRate || '',
      companyWebsiteUrl: initialCompanyData?.companyWebsiteUrl || '',
      facilityType: initialCompanyData?.facilityType || '',
      contactPersonName: initialCompanyData?.contact?.contactPersonName || '',
      email: initialCompanyData?.contact?.email || '',
      phone: initialCompanyData?.contact?.phone || '',
    });
  }, [initialCompanyData, reset]);

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const onSubmit = async (data) => {
  
    try {
      await updateCustomerCompanyDetails(providerCompanyId, companyData._id, {
        companyLegalName: data.companyLegalName,
        companyFriendlyName: data.companyFriendlyName,
        hourlyBillingRate: data.hourlyBillingRate,
        companyWebsiteUrl: data.companyWebsiteUrl,
        facilityType: data.facilityType,
        contactPersonName: data.contactPersonName,
        phone: data.phone,
        email: data.email,
        customer_company_id: companyData.customer_company_id // global copy of company Id 
      });

      setCompanyData((prevData) => ({
        ...prevData,
        companyLegalName: data.companyLegalName,
        companyFriendlyName: data.companyFriendlyName,
        hourlyBillingRate: data.hourlyBillingRate,
        companyWebsiteUrl: data.companyWebsiteUrl,
        facilityType: data.facilityType,
        contact: {
          contactPersonName: data.contactPersonName,
          phone: data.phone,
          email: data.email,
        },
      }));

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update company details:', error);
    }
  };

  return (
    <>
      {!isEditing ? (
        <div className="font-inter space-y-4">
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Legal Name</label>
            <p className="text-[var(--foreground)] font-medium text-sm">{companyData.companyLegalName || 'Not specified'}</p>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Friendly Name</label>
            <p className="text-[var(--foreground)] font-medium text-sm">{companyData.companyFriendlyName || 'Not specified'}</p>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Hourly Billing Rate</label>
            <div className="flex items-center gap-2">
              <DollarSign className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              <p className="text-[var(--foreground)] font-semibold text-sm">
                {companyData.preferences?.hourlyBillingRate ? `$${companyData.preferences.hourlyBillingRate}/hr` : 'Not specified'}
              </p>
            </div>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Facility Type</label>
            <div className="flex items-center gap-2">
              <Building className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              <p className="text-[var(--foreground)] font-medium capitalize text-sm">
                {companyData.facilityType || 'Not specified'}
              </p>
            </div>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Website</label>
            <div className="flex items-center gap-2">
              <Globe className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              {companyData.companyWebsiteUrl ? (
                <a 
                  href={companyData.companyWebsiteUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[var(--primary)] font-medium hover:underline text-sm"
                >
                  {companyData.companyWebsiteUrl}
                </a>
              ) : (
                <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
              )}
            </div>
          </div>

          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Contact Person</label>
            <p className="text-[var(--foreground)] font-medium text-sm">{companyData.contact?.contactPersonName || 'Not specified'}</p>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Email Address</label>
            <div className="flex items-center gap-2">
              <Mail className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              {companyData.contact?.email ? (
                <a 
                  href={`mailto:${companyData.contact.email}`}
                  className="text-[var(--primary)] font-medium hover:underline text-sm"
                >
                  {companyData.contact.email}
                </a>
              ) : (
                <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
              )}
            </div>
          </div>
          
          <div className="space-y-0.5">
            <label className="text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wide">Phone Number</label>
            <div className="flex items-center gap-2">
              <Phone className="text-[var(--brand-pink)]" style={{ fontSize: '14px' }} />
              {companyData.contact?.phone ? (
                <a 
                  href={`tel:${companyData.contact.phone}`}
                  className="text-[var(--primary)] font-medium hover:underline text-sm"
                >
                  {companyData.contact.phone}
                </a>
              ) : (
                <p className="text-[var(--foreground)] font-medium text-sm">Not specified</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-[var(--border)]">
            <button 
              type="button"
              onClick={toggleEditMode} 
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[var(--primary)] bg-transparent border border-[var(--primary)] rounded-lg hover:bg-[var(--primary)] hover:text-white transition-all duration-200 font-medium text-sm"
            >
              <Pencil style={{ fontSize: '16px' }} />
              Edit Details
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="configure-form space-y-3">
          {/* Company Information Section */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-xs font-normal text-gray-600 mb-2 flex items-center gap-1">
              <Building2 style={{ fontSize: '14px' }} />
              Company Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Company Legal Name</label>
                <input type="text" {...register('companyLegalName')} className="form-input text-sm py-1.5" placeholder="Amazon Inc." />
                {errors.companyLegalName && <span className="form-error text-xs">{errors.companyLegalName.message}</span>}
              </div>

              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Friendly Name</label>
                <input type="text" {...register('companyFriendlyName')} className="form-input text-sm py-1.5" placeholder="Amazon Warehouse" />
                {errors.companyFriendlyName && <span className="form-error text-xs">{errors.companyFriendlyName.message}</span>}
              </div>

              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Hourly Billing Rate ($)</label>
                <input type="number" {...register('hourlyBillingRate')} className="form-input text-sm py-1.5" placeholder={48} />
                {errors.hourlyBillingRate && <span className="form-error text-xs">{errors.hourlyBillingRate.message}</span>}
              </div>

              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Facility Type</label>
                <select {...register("facilityType")} className="form-select text-sm py-1.5">
                  <option value="store">Store</option>
                  <option value="workplace">Workplace</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="office">Office</option>
                  <option value="factory">Factory</option>
                  <option value="construction">Construction Site</option>
                  <option value="other">Other</option>
                </select>
                {errors.facilityType && <span className="form-error text-xs">{errors.facilityType.message}</span>}
              </div>

              <div className="form-group mb-2 md:col-span-2">
                <label className="form-label text-xs mb-1">Company Website URL</label>
                <input type="text" {...register('companyWebsiteUrl')} className="form-input text-sm py-1.5" placeholder="https://www.amazon.com" />
                {errors.companyWebsiteUrl && <span className="form-error text-xs">{errors.companyWebsiteUrl.message}</span>}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-xs font-normal text-gray-600 mb-2 flex items-center gap-1">
              <User style={{ fontSize: '14px' }} />
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Contact Person</label>
                <input type="text" {...register('contactPersonName')} className="form-input text-sm py-1.5" placeholder="John Doe" />
                {errors.contactPersonName && <span className="form-error text-xs">{errors.contactPersonName.message}</span>}
              </div>

              <div className="form-group mb-2">
                <label className="form-label text-xs mb-1">Email</label>
                <input type="email" {...register('email')} className="form-input text-sm py-1.5" placeholder="john.doe@example.com" />
                {errors.email && <span className="form-error text-xs">{errors.email.message}</span>}
              </div>

              <div className="form-group phone_field mb-2 md:col-span-2">
                <label className="form-label text-xs mb-1">Phone</label>
                <Controller name="phone" control={control} render={({ field }) => (
                  <PhoneInput defaultCountry="US" value={field.value} onChange={field.onChange}  />
                )} />
                {errors.phone && <span className="form-error text-xs">{errors.phone.message}</span>}
              </div>
            </div>
          </div>

          <div className="text-right">
            <button type="submit" className="staffing-btn confirm-btn">Save</button>
            <button type="button" className="staffing-btn cancel-btn ml-2" onClick={toggleEditMode}>Cancel</button>
          </div>
        </form>
      )}
    </>
  );
};

export default ContactInfo;
